import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Send } from "lucide-react";
import {
    listChannels, createChannel, deleteChannel,
    listCampaigns, createCampaign, deleteCampaign,
    listClips, ingestEvent, runCleanup,
} from "../lib/api";

const TIERS = ["free_public", "edgy_public", "premium_private", "internal_test"];
const PLATFORMS = ["tiktok", "instagram", "facebook", "youtube_shorts", "x", "ifunny", "other"];
const GOALS = ["audience_growth", "retention_testing", "darkness_level_testing", "platform_fit_testing", "premium_conversion_testing"];

const Tabs = ({ value, onChange }) => {
    const tabs = ["channels", "campaigns", "ingest", "admin"];
    return (
        <div className="flex gap-px bg-[#262626] border-b border-[#262626]">
            {tabs.map((t) => (
                <button
                    key={t}
                    data-testid={`ops-tab-${t}`}
                    onClick={() => onChange(t)}
                    className={`flex-1 md:flex-none md:px-6 py-3 font-mono-d8 text-xs uppercase tracking-[0.25em] transition-colors ${
                        value === t ? "bg-[#ff2a2a] text-[#f5f5f0]" : "bg-[#050505] text-[#8a8a8a] hover:text-[#f5f5f0]"
                    }`}
                >
                    {t}
                </button>
            ))}
        </div>
    );
};

const Input = ({ testid, ...p }) => (
    <input
        data-testid={testid}
        {...p}
        className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3 focus:outline-none focus:border-[#ff2a2a]"
    />
);

const Select = ({ testid, options, format, ...p }) => (
    <select
        data-testid={testid}
        {...p}
        className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm uppercase tracking-wider p-3 focus:outline-none focus:border-[#ff2a2a]"
    >
        {options.map((o) => (
            <option key={o} value={o}>{format ? format(o) : String(o).replace(/_/g, " ")}</option>
        ))}
    </select>
);

// ---- Channels tab -----------------------------------------------------------

const ChannelsTab = () => {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ channel_name: "", platform: "tiktok", handle: "", url: "", monetization_status: "none", audience_type: "general", content_tier: "free_public" });

    const load = () => listChannels().then(setItems);
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.channel_name.trim()) return toast.error("Name required");
        try {
            await createChannel(form);
            setForm({ ...form, channel_name: "", handle: "", url: "" });
            toast.success("Channel created");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const del = async (id) => {
        try {
            await deleteChannel(id);
            load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Channel delete failed");
        }
    };

    return (
        <div data-testid="channels-tab" className="grid lg:grid-cols-[380px_1fr] gap-px bg-[#262626]">
            <aside className="bg-[#050505] p-6 space-y-4">
                <div className="d8-label">New channel</div>
                <Input testid="ch-name" placeholder="Channel name" value={form.channel_name} onChange={(e) => setForm({ ...form, channel_name: e.target.value })} />
                <Select testid="ch-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} options={PLATFORMS} />
                <Input testid="ch-handle" placeholder="@handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} />
                <Input testid="ch-url" placeholder="https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
                <Input testid="ch-monetization" placeholder="monetization status" value={form.monetization_status} onChange={(e) => setForm({ ...form, monetization_status: e.target.value })} />
                <Input testid="ch-audience" placeholder="audience type" value={form.audience_type} onChange={(e) => setForm({ ...form, audience_type: e.target.value })} />
                <Select testid="ch-tier" value={form.content_tier} onChange={(e) => setForm({ ...form, content_tier: e.target.value })} options={TIERS} />
                <button
                    data-testid="ch-create"
                    onClick={submit}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest py-3"
                >
                    <Plus className="w-4 h-4" /> Create
                </button>
            </aside>
            <main className="bg-[#050505] p-6">
                <div className="d8-label mb-4">Channels · {items.length}</div>
                {items.length === 0 && <div data-testid="channels-empty" className="border border-dashed border-[#262626] p-8 text-center text-[#8a8a8a] text-sm">No channels yet.</div>}
                <div className="grid gap-px bg-[#262626]">
                    {items.map((c) => (
                        <div key={c.channel_id} data-testid={`ch-row-${c.channel_id}`} className="bg-[#0a0a0a] p-4 flex items-center justify-between gap-3">
                            <div>
                                <div className="font-heading font-bold uppercase text-[#f5f5f0]">{c.channel_name}</div>
                                <div className="font-mono-d8 text-[11px] text-[#8a8a8a] uppercase tracking-wider">
                                    {c.platform} · {c.handle || "—"} · {c.content_tier}
                                </div>
                            </div>
                            <button
                                data-testid={`ch-del-${c.channel_id}`}
                                onClick={() => del(c.channel_id)}
                                className="text-[#8a8a8a] hover:text-[#ff2a2a]"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </main>
        </div>
    );
};

// ---- Campaigns tab ----------------------------------------------------------

const CampaignsTab = () => {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ campaign_name: "", description: "", target_platforms: ["tiktok"], content_tier: "free_public", goal: "audience_growth" });

    const load = () => listCampaigns().then(setItems);
    useEffect(() => { load(); }, []);

    const togglePlatform = (p) =>
        setForm((f) => ({
            ...f,
            target_platforms: f.target_platforms.includes(p)
                ? f.target_platforms.filter((x) => x !== p)
                : [...f.target_platforms, p],
        }));

    const submit = async () => {
        if (!form.campaign_name.trim()) return toast.error("Name required");
        try {
            await createCampaign(form);
            setForm({ ...form, campaign_name: "", description: "" });
            toast.success("Campaign created");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const del = async (id) => {
        try {
            await deleteCampaign(id);
            load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Campaign delete failed");
        }
    };

    return (
        <div data-testid="campaigns-tab" className="grid lg:grid-cols-[380px_1fr] gap-px bg-[#262626]">
            <aside className="bg-[#050505] p-6 space-y-4">
                <div className="d8-label">New campaign</div>
                <Input testid="cm-name" placeholder="Campaign name" value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} />
                <textarea
                    data-testid="cm-desc"
                    placeholder="description"
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3 focus:outline-none focus:border-[#ff2a2a]"
                />
                <div className="space-y-1">
                    <div className="d8-label">Target platforms</div>
                    <div className="flex flex-wrap gap-1.5">
                        {PLATFORMS.map((p) => (
                            <button
                                key={p}
                                data-testid={`cm-platform-${p}`}
                                onClick={() => togglePlatform(p)}
                                className={`font-mono-d8 text-[10px] uppercase tracking-widest px-2 py-1 border ${
                                    form.target_platforms.includes(p) ? "bg-[#ff2a2a] border-[#ff2a2a] text-[#f5f5f0]" : "border-[#262626] text-[#8a8a8a] hover:text-[#f5f5f0]"
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
                <Select testid="cm-tier" value={form.content_tier} onChange={(e) => setForm({ ...form, content_tier: e.target.value })} options={TIERS} />
                <Select testid="cm-goal" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} options={GOALS} />
                <button
                    data-testid="cm-create"
                    onClick={submit}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest py-3"
                >
                    <Plus className="w-4 h-4" /> Create
                </button>
            </aside>
            <main className="bg-[#050505] p-6">
                <div className="d8-label mb-4">Campaigns · {items.length}</div>
                {items.length === 0 && <div data-testid="campaigns-empty" className="border border-dashed border-[#262626] p-8 text-center text-[#8a8a8a] text-sm">No campaigns yet.</div>}
                <div className="grid gap-px bg-[#262626]">
                    {items.map((c) => (
                        <div key={c.campaign_id} data-testid={`cm-row-${c.campaign_id}`} className="bg-[#0a0a0a] p-4 flex items-center justify-between gap-3">
                            <div>
                                <div className="font-heading font-bold uppercase text-[#f5f5f0]">{c.campaign_name}</div>
                                <div className="font-mono-d8 text-[11px] text-[#8a8a8a] uppercase tracking-wider">
                                    {c.goal} · {c.content_tier} · {(c.target_platforms || []).join(", ")}
                                </div>
                            </div>
                            <button
                                data-testid={`cm-del-${c.campaign_id}`}
                                onClick={() => del(c.campaign_id)}
                                className="text-[#8a8a8a] hover:text-[#ff2a2a]"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </main>
        </div>
    );
};

// ---- Ingest tab -------------------------------------------------------------

const IngestTab = () => {
    const [clips, setClips] = useState([]);
    const [channels, setChannels] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [form, setForm] = useState({
        clip_id: "",
        channel_id: "",
        platform: "tiktok",
        campaign_id: "",
        campaign_name: "",
        post_url: "",
        impressions: 10000, views: 4500, average_watch_time: 5.2,
        completion_rate: 0.42, rewatches: 320, shares: 180,
        saves: 220, comments: 90, likes: 600,
        dislikes_or_negative_feedback_count: 10, reported_count: 2,
        follower_gain: 18,
    });

    useEffect(() => {
        listClips({ saved: true }).then(setClips).catch(() => setClips([]));
        listChannels().then(setChannels).catch(() => {});
        listCampaigns().then(setCampaigns).catch(() => {});
    }, []);

    const submit = async () => {
        if (!form.clip_id) return toast.error("Pick a clip");
        try {
            const payload = { ...form };
            if (!payload.channel_id) delete payload.channel_id;
            if (!payload.campaign_id) delete payload.campaign_id;
            if (!payload.campaign_name) delete payload.campaign_name;
            if (!payload.post_url) delete payload.post_url;
            await ingestEvent(payload);
            toast.success("Event ingested");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div data-testid="ingest-tab" className="p-6 bg-[#050505] space-y-5 max-w-3xl">
            <div className="d8-label">Analytics ingest (manual test)</div>
            <p className="text-[#8a8a8a] text-sm">
                Use this to pipe a real post's numbers into Dark8-s while an automated scraper is not yet wired.
                Server rejects unknown clip_id.
            </p>

            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <div className="d8-label mb-1">Clip</div>
                    <select
                        data-testid="ing-clip"
                        value={form.clip_id}
                        onChange={(e) => setForm({ ...form, clip_id: e.target.value })}
                        className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3"
                    >
                        <option value="">— choose saved clip —</option>
                        {clips.map((c) => (
                            <option key={c.id} value={c.id}>{c.title}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <div className="d8-label mb-1">Channel (optional)</div>
                    <select
                        data-testid="ing-channel"
                        value={form.channel_id}
                        onChange={(e) => setForm({ ...form, channel_id: e.target.value })}
                        className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3"
                    >
                        <option value="">—</option>
                        {channels.map((c) => (
                            <option key={c.channel_id} value={c.channel_id}>{c.channel_name}</option>
                        ))}
                    </select>
                </div>
                <Select testid="ing-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} options={PLATFORMS} />
                <div>
                    <div className="d8-label mb-1">Campaign (optional)</div>
                    <select
                        data-testid="ing-campaign"
                        value={form.campaign_id}
                        onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}
                        className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3"
                    >
                        <option value="">—</option>
                        {campaigns.map((c) => (
                            <option key={c.campaign_id} value={c.campaign_id}>{c.campaign_name}</option>
                        ))}
                    </select>
                </div>
                <Input testid="ing-url" placeholder="post_url (https://)" value={form.post_url} onChange={(e) => setForm({ ...form, post_url: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                    ["impressions", "number"],
                    ["views", "number"],
                    ["average_watch_time", "number"],
                    ["completion_rate", "number"],
                    ["rewatches", "number"],
                    ["shares", "number"],
                    ["saves", "number"],
                    ["comments", "number"],
                    ["likes", "number"],
                    ["dislikes_or_negative_feedback_count", "number"],
                    ["reported_count", "number"],
                    ["follower_gain", "number"],
                ].map(([k, t]) => (
                    <div key={k}>
                        <div className="d8-label mb-1">{k.replace(/_/g, " ")}</div>
                        <input
                            data-testid={`ing-${k}`}
                            type={t}
                            step="any"
                            value={form[k]}
                            onChange={(e) => setForm({ ...form, [k]: t === "number" ? Number(e.target.value) : e.target.value })}
                            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-2"
                        />
                    </div>
                ))}
            </div>

            <button
                data-testid="ing-submit"
                onClick={submit}
                className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3"
            >
                <Send className="w-4 h-4" /> Ingest event
            </button>
        </div>
    );
};

// ---- Admin tab --------------------------------------------------------------

const AdminTab = () => {
    const [last, setLast] = useState(null);
    const go = async () => {
        try {
            const r = await runCleanup();
            setLast(r);
            toast.success(`Deleted ${r.deleted} unsaved clips (TTL ${r.ttl_days}d)`);
        } catch (e) {
            toast.error("Cleanup failed");
        }
    };
    return (
        <div data-testid="admin-tab" className="p-6 bg-[#050505] space-y-5 max-w-2xl">
            <div className="d8-label">Unsaved clip cleanup</div>
            <p className="text-[#8a8a8a] text-sm">
                Deletes clips where <span className="font-mono-d8 text-[#f5f5f0]">saved != true</span> and
                older than <span className="font-mono-d8 text-[#f5f5f0]">UNSAVED_CLIP_TTL_DAYS</span>.
                Saved clips are never touched.
            </p>
            <button
                data-testid="admin-cleanup"
                onClick={go}
                className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3"
            >
                Run cleanup now
            </button>
            {last && (
                <pre className="bg-[#0a0a0a] border border-[#262626] p-4 font-mono-d8 text-[11px] text-[#f5f5f0] overflow-x-auto">
                    {JSON.stringify(last, null, 2)}
                </pre>
            )}
        </div>
    );
};

// ---- Page -------------------------------------------------------------------

export const Operations = () => {
    const [tab, setTab] = useState("channels");
    return (
        <div data-testid="operations-page" className="min-h-screen">
            <Toaster theme="dark" position="top-right" />
            <section className="border-b border-[#262626] px-6 md:px-12 py-10 noise-bg">
                <div className="d8-label mb-3">/ Operations</div>
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                    Content ops
                </h1>
                <p className="mt-4 text-[#8a8a8a] max-w-2xl">
                    Manage branded channels, campaigns, and real analytics ingest. Everything here is treated as untrusted input — server-side validated, size-capped, reference-checked.
                </p>
            </section>
            <Tabs value={tab} onChange={setTab} />
            {tab === "channels" && <ChannelsTab />}
            {tab === "campaigns" && <CampaignsTab />}
            {tab === "ingest" && <IngestTab />}
            {tab === "admin" && <AdminTab />}
        </div>
    );
};

export default Operations;
